import React, { useState, useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router";
import { FormikHelpers } from "formik";
import { diff } from "json8-patch";

import PageLayout from "../../_layout/PageLayout";
import Form from "../../../components/form/Form";
import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import {
  DisplaySewerObjectId,
  DisplayDataOwner,
  DisplayInProject,
  DisplayOpenJobs,
} from "../components/DataComponents";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import { EditDynamicProperties } from "../../../components/data-components/EditDynamicProperties";
import useUrlParams from "../../../services/url/UrlParamsService";
import { api } from "../../../services/api/ApiService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import {
  EditSewerObject,
  EditSewerObjectFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { NodeDynamicMenuItemsPages, NodeMenuItemsModel } from "./NodeMenuItems";
import { CurrentNodeStorageModel } from "../../../services/sewer-object/CurrentNodeStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { navigatePath } from "../../../helpers/StringHelpers";
import useRouteService from "../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { FieldsFromBM } from "../../../api-models/response/BluemetricFields";

export default observer(function EditNode() {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentNodeStorage] = useState(() =>
    CurrentNodeStorageModel.create({
      nodeId: urlParamsService.getNodeId()!,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [nodeMenuItems] = useState(() =>
    NodeMenuItemsModel.create(
      {
        nodeId: urlParamsService.getNodeId()!,
        dynamicMenuItemPage: NodeDynamicMenuItemsPages.EditNode,
        parentIdFromPath: urlParamsService.getSewerObjectParentId()!,
      },
      { api }
    )
  );

  const canUserEditNode = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        EditSewerObject,
        EditSewerObjectFromAssignedProject,
      ]),
    []
  );

  const goToNodePage = useCallback(
    () => history.push(`${navigatePath(history.location.pathname, -1)}`),
    [history]
  );

  const getInitialValues = useCallback(
    () => currentNodeStorage.currentNode.getDynamicPropertiesKeyValueForEdit(),
    [currentNodeStorage]
  );

  const menuItems = {
    staticItems: nodeMenuItems.getStaticMenuItems(),
  };

  const handleSubmit = async (
    updatedDynamicProperties: FieldsFromBM,
    formActions: FormikHelpers<FieldsFromBM>
  ) => {
    try {
      const cleanedUpdatedDynamicProperties =
        currentNodeStorage.currentNode.getDynamicPropertiesWithDecimalsAndDateFormatted(
          updatedDynamicProperties
        );
      let patchJson = diff(getInitialValues(), cleanedUpdatedDynamicProperties);
      await currentNodeStorage.patchDetails(patchJson);
      warningLeavePageService.setIsDirty(false);
      return goToNodePage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(updatedDynamicProperties, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentNodeStorage.parentEntityFromPath.type,
        name: await currentNodeStorage.getParentEntityName(currentNodeStorage.currentNode.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentNodeStorage.currentNode.name
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentNodeStorage, currentNodeStorage.currentNode, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditSewerObject,EditSewerObjectFromAssignedProject]}
      titleKey="EditNode.title"
      titleContext={{ nodeName: currentNodeStorage.currentNode.name }}
      id="edit-node"
      menuItems={menuItems}
      isLoading={currentNodeStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        canSubmit={canUserEditNode}
        onCancel={goToNodePage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.node.details">
                <SubSection>
                  <DisplaySewerObjectId
                    id={currentNodeStorage.currentNode.name}
                    labelKey="sewer-objects.DisplayNodeId.label"
                  />
                  <DisplayDataOwner dataOwner={currentNodeStorage.currentNode.dataOwner} />
                </SubSection>
                <SubSection>
                  <DisplayInProject project={currentNodeStorage.currentNode.project} />
                  <DisplayOpenJobs openJobs={currentNodeStorage.currentNode.openJobs} />
                </SubSection>
                <SubSection>
                  <DisplayDateCreated date={currentNodeStorage.currentNode.created} />
                  <DisplayCreatedBy createdBy={currentNodeStorage.currentNode.createdBy.name} />
                </SubSection>
              </DataSection>
              <EditDynamicProperties
                labelKey="general.characteristics"
                properties={currentNodeStorage.currentNode.getDynamicPropertiesForEdit()}
                values={values}
                setFieldValue={setFieldValue}
              />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
